import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Workspace preferences: theme, navigation state, density, saved views.
 *
 * Persisted to localStorage so an analyst's workspace survives a reload. Every
 * read is wrapped because storage throws in private windows and in embedded
 * preview contexts.
 */

const THEME_KEY = 'socialpulse.theme'

function systemPrefersDark() {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  } catch {
    return false
  }
}

function readStoredTheme() {
  try {
    return localStorage.getItem(THEME_KEY)
  } catch {
    return null
  }
}

/** Stamp the resolved theme on <html> so the token layer can respond. */
export function applyTheme(theme) {
  const resolved = theme === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : theme
  document.documentElement.setAttribute('data-theme', resolved)
  try { localStorage.setItem(THEME_KEY, theme) } catch { /* storage unavailable */ }
  return resolved
}

export const useUI = create(
  persist(
    (set, get) => ({
      theme: readStoredTheme() || 'system',
      setTheme: (theme) => { applyTheme(theme); set({ theme }) },
      cycleTheme: () => {
        const order = ['light', 'dark', 'system']
        const next = order[(order.indexOf(get().theme) + 1) % order.length]
        applyTheme(next)
        set({ theme: next })
      },

      navCollapsed: false,
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),

      /**
       * Sprint 8: sidebar drag-to-resize width, in px, applied only when
       * `navCollapsed` is false (the collapsed width stays the fixed
       * `--nav-w-collapsed` token). Clamped so a wild drag can never make the
       * sidebar unusably narrow or eat the whole viewport.
       */
      sidebarWidth: 240,
      setSidebarWidth: (px) => set({ sidebarWidth: Math.min(420, Math.max(180, Math.round(px))) }),

      filterBarOpen: true,
      toggleFilterBar: () => set((s) => ({ filterBarOpen: !s.filterBarOpen })),

      /**
       * Show statistical uncertainty throughout the product.
       *
       * When on, every metric that reports an interval renders it, and a delta
       * that fails its significance test is labelled "not significant" instead
       * of getting an arrow that implies a real move. Default on: this is an
       * analysis tool, and hiding uncertainty is how dashboards mislead.
       */
      showConfidence: true,
      toggleConfidence: () => set((s) => ({ showConfidence: !s.showConfidence })),

      commandOpen: false,
      setCommandOpen: (open) => set({ commandOpen: open }),

      /**
       * Saved filter views — the analyst's own named slices of the dataset.
       *
       * Stored per tenant (`tenantId` on each view): a view's topic/brand/scope
       * values are another tenant's vocabulary, so restoring Acme's
       * "Syriatel + network" view inside Northwind would only empty every
       * chart. Read them through `viewsForTenant`. Views saved before this
       * field existed carry no tenant and are hidden rather than guessed at.
       */
      savedViews: [],
      saveView: (name, filters, path, tenantId = null) =>
        set((s) => ({
          savedViews: [
            ...s.savedViews.filter((v) => !(v.name === name && (v.tenantId ?? null) === tenantId)),
            { name, filters, path, tenantId, createdAt: new Date().toISOString() },
          ],
        })),
      deleteView: (name, tenantId = null) => set((s) => ({
        savedViews: s.savedViews.filter((v) => !(v.name === name && (v.tenantId ?? null) === tenantId)),
      })),

      /** Pages the analyst pinned to the top of the sidebar. */
      pinned: [],
      togglePin: (path) =>
        set((s) => ({
          pinned: s.pinned.includes(path) ? s.pinned.filter((p) => p !== path) : [...s.pinned, path],
        })),

      recentPosts: [],
      pushRecentPost: (id) =>
        set((s) => ({ recentPosts: [id, ...s.recentPosts.filter((x) => x !== id)].slice(0, 12) })),
    }),
    {
      name: 'socialpulse.workspace',
      partialize: (s) => ({
        theme: s.theme,
        navCollapsed: s.navCollapsed,
        sidebarWidth: s.sidebarWidth,
        filterBarOpen: s.filterBarOpen,
        showConfidence: s.showConfidence,
        savedViews: s.savedViews,
        pinned: s.pinned,
      }),
    },
  ),
)

/** The saved views that belong to one tenant (see `savedViews` above). */
export function viewsForTenant(savedViews, tenantId) {
  if (!Array.isArray(savedViews)) return []
  return savedViews.filter((v) => (v.tenantId ?? null) === (tenantId ?? null))
}
